import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    BarChart2, RefreshCw, Banknote, CreditCard, Smartphone,
    ArrowRightLeft, TrendingUp, ShoppingCart, Users, Calendar,
    XCircle, AlertTriangle, Tag, Package, Printer, Boxes,
} from 'lucide-react';
import { openListaProductos } from '../../utils/printListaProductos';

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/inventario`;

const FORMA_META = {
    efectivo:      { label: 'Efectivo',      icon: <Banknote size={18} />,       color: 'marca' },
    transferencia: { label: 'Transferencia', icon: <ArrowRightLeft size={18} />, color: 'marca' },
    tarjeta:       { label: 'Tarjeta',       icon: <CreditCard size={18} />,     color: 'marca' },
    qr:            { label: 'QR',            icon: <Smartphone size={18} />,     color: 'marca' },
};

const COLOR = {
    // Un indicador comun. El color no distingue una metrica de otra: para eso
    // estan su titulo y su icono.
    marca:    'bg-brand-50 border-brand-200 text-brand-800 dark:bg-brand-500/10 dark:border-brand-500/20 dark:text-brand-400',
    // Informa algo que anda mal. Aca el rojo si esta diciendo algo.
    problema: 'bg-error-50 border-error-200 text-error-700 dark:bg-error-500/10 dark:border-error-500/20 dark:text-error-400',
    // Dato de contexto, sin peso propio.
    neutro:   'bg-slate-50 border-slate-200 text-slate-700 dark:bg-slate-800/60 dark:border-white/10 dark:text-slate-300',
};

const fmt = (n) => Number(n || 0).toLocaleString('es-PY');

function today() {
    return new Date().toISOString().slice(0, 10);
}
function firstOfMonth() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

export default function Reportes() {

    const [tab, setTab] = useState('ventas'); // 'ventas' | 'anulaciones' | 'variacion' | 'productos'
    const [desde, setDesde] = useState(firstOfMonth());
    const [hasta, setHasta] = useState(today());
    const [data, setData] = useState(null);
    const [dataAnul, setDataAnul] = useState(null);
    const [dataVar, setDataVar] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [filtroTipo, setFiltroTipo] = useState('todos'); // 'todos' | 'costo' | 'venta'

    // El listado de artículos no depende del período: es una foto de lo que
    // hay hoy. Por eso se pide aparte y no junto con los otros tres.
    const [dataProd, setDataProd] = useState(null);
    const [soloReponer, setSoloReponer] = useState(false);
    const [settings, setSettings] = useState(null);

    const fetchReporte = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [resVentas, resAnul, resVar] = await Promise.all([
                axios.get(`${API}/reportes/ventas/`, { params: { desde, hasta } }),
                axios.get(`${API}/reportes/anulaciones/`, { params: { desde, hasta } }),
                axios.get(`${API}/reportes/variacion-precios/`, { params: { desde, hasta } }),
            ]);
            setData(resVentas.data);
            setDataAnul(resAnul.data);
            setDataVar(resVar.data);
        } catch (e) {
            setError(e.response?.data?.error || 'Error al cargar reporte.');
        } finally {
            setLoading(false);
        }
    }, [desde, hasta]);

    useEffect(() => { fetchReporte(); }, [fetchReporte]);

    const fetchProductos = useCallback(async () => {
        try {
            const [resProd, resSettings] = await Promise.all([
                axios.get(`${API}/reportes/productos/`, { params: { solo_reponer: soloReponer } }),
                axios.get(`${API}/settings/`),
            ]);
            setDataProd(resProd.data);
            setSettings(resSettings.data);
        } catch (e) {
            setError(e.response?.data?.error || 'Error al cargar el listado de artículos.');
        }
    }, [soloReponer]);

    // Se pide solo al entrar a su pestaña: el catálogo de una ferretería puede
    // tener miles de artículos y no hace falta traerlos para mirar ventas.
    useEffect(() => {
        if (tab === 'productos') fetchProductos();
    }, [tab, fetchProductos]);

    const maxDia = data ? Math.max(...data.por_dia.map(d => d.total), 1) : 1;

    return (
        <div className="max-w-5xl mx-auto space-y-6 pb-10">
            {/* Cabecera */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                        <BarChart2 size={24} className="text-brand-800" /> Reportes de Ventas
                    </h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Analizá tus ventas por período, forma de pago y vendedor.</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-slate-200 dark:border-white/10 pb-0">
                {[
                    { key: 'ventas',     label: 'Ventas',              icon: <ShoppingCart size={15} /> },
                    { key: 'anulaciones',label: 'Anulaciones',         icon: <XCircle size={15} /> },
                    { key: 'variacion',  label: 'Variación de Precios',icon: <TrendingUp size={15} /> },
                    { key: 'productos',  label: 'Artículos',            icon: <Boxes size={15} /> },
                ].map(t => (
                    <button key={t.key} onClick={() => setTab(t.key)}
                        className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all -mb-px ${tab === t.key
                            ? 'border-brand-500 text-brand-800 dark:text-brand-400'
                            : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'}`}>
                        {t.icon}{t.label}
                        {t.key === 'anulaciones' && dataAnul?.total_anulaciones > 0 && (
                            <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold bg-error-100 dark:bg-error-500/20 text-error-500 dark:text-error-400 rounded-full">
                                {dataAnul.total_anulaciones}
                            </span>
                        )}
                        {t.key === 'variacion' && dataVar?.alerta_sin_ajuste_venta > 0 && (
                            <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold bg-alerta-100 dark:bg-alerta-500/20 text-alerta-600 dark:text-alerta-400 rounded-full">
                                {dataVar.alerta_sin_ajuste_venta}
                            </span>
                        )}
                        {t.key === 'productos' && dataProd?.a_reponer > 0 && (
                            <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold bg-alerta-100 dark:bg-alerta-500/20 text-alerta-600 dark:text-alerta-400 rounded-full">
                                {dataProd.a_reponer}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* Filtros */}
            <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl p-5 shadow-sm">
                <div className="flex flex-wrap items-end gap-4">
                    <div className="flex-1 min-w-[140px]">
                        <label htmlFor="reporte-desde" className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1.5 flex items-center gap-1">
                            <Calendar size={12} /> Desde
                        </label>
                        <input id="reporte-desde"
                            type="date"
                            value={desde}
                            onChange={e => setDesde(e.target.value)}
                            className="w-full px-3 py-2.5 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                        />
                    </div>
                    <div className="flex-1 min-w-[140px]">
                        <label htmlFor="reporte-hasta" className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1.5 flex items-center gap-1">
                            <Calendar size={12} /> Hasta
                        </label>
                        <input id="reporte-hasta"
                            type="date"
                            value={hasta}
                            onChange={e => setHasta(e.target.value)}
                            className="w-full px-3 py-2.5 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                        />
                    </div>
                    <button
                        onClick={fetchReporte}
                        disabled={loading}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-500 disabled:opacity-60 text-negro-950 text-sm font-semibold transition-all"
                    >
                        <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
                        {loading ? 'Cargando...' : 'Actualizar'}
                    </button>
                </div>
                {/* Atajos de período */}
                <div className="flex flex-wrap gap-2 mt-3">
                    {[
                        { label: 'Hoy', fn: () => { setDesde(today()); setHasta(today()); } },
                        { label: 'Este mes', fn: () => { setDesde(firstOfMonth()); setHasta(today()); } },
                        { label: 'Últimos 7 días', fn: () => {
                            const d = new Date(); d.setDate(d.getDate() - 6);
                            setDesde(d.toISOString().slice(0, 10)); setHasta(today());
                        }},
                        { label: 'Últimos 30 días', fn: () => {
                            const d = new Date(); d.setDate(d.getDate() - 29);
                            setDesde(d.toISOString().slice(0, 10)); setHasta(today());
                        }},
                    ].map(({ label, fn }) => (
                        <button key={label} onClick={fn}
                            className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10 font-medium transition-all">
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            {error && (
                <div className="bg-error-50 dark:bg-error-500/10 border border-error-200 dark:border-error-500/20 text-error-600 dark:text-error-400 rounded-xl p-4 text-sm font-semibold">
                    {error}
                </div>
            )}

            {data && tab === 'ventas' && (
                <>
                    {/* KPIs */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                        <KpiCard
                            label="Total Ventas"
                            value={`Gs. ${fmt(data.total_ventas)}`}
                            icon={<TrendingUp size={20} />}
                            color="marca"
                        />
                        <KpiCard
                            label="Cantidad de Ventas"
                            value={data.cantidad_ventas}
                            icon={<ShoppingCart size={20} />}
                            color="marca"
                        />
                        <KpiCard
                            label="Promedio por Venta"
                            value={`Gs. ${fmt(data.promedio_venta)}`}
                            icon={<BarChart2 size={20} />}
                            color="marca"
                            className="col-span-2 sm:col-span-1"
                        />
                    </div>

                    {/* Desglose por forma de pago */}
                    <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl p-5 shadow-sm">
                        <h3 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">
                            Por Forma de Pago
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                            {Object.entries(FORMA_META).map(([key, meta]) => {
                                const d = data.desglose_pago?.[key] || { total: 0, cantidad: 0 };
                                const pct = data.total_ventas > 0 ? Math.round((d.total / data.total_ventas) * 100) : 0;
                                return (
                                    <div key={key} className={`p-4 rounded-xl border ${COLOR[meta.color]}`}>
                                        <div className="flex items-center gap-2 mb-2 opacity-80">{meta.icon} <span className="text-xs font-bold uppercase tracking-wide">{meta.label}</span></div>
                                        <p className="text-lg font-bold">Gs. {fmt(d.total)}</p>
                                        <p className="text-xs opacity-60 mt-0.5">{d.cantidad} venta{d.cantidad !== 1 ? 's' : ''} · {pct}%</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Transacciones agrupadas por forma de pago */}
                    {data.por_forma_pago && Object.values(data.por_forma_pago).some(g => g.length > 0) && (
                        <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm">
                            <div className="px-5 py-4 border-b border-slate-100 dark:border-white/10">
                                <h3 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Transacciones por Forma de Pago</h3>
                            </div>
                            {Object.entries(FORMA_META).map(([key, meta]) => {
                                const grupo = data.por_forma_pago?.[key] || [];
                                if (grupo.length === 0) return null;
                                const subtotal = grupo.reduce((a, v) => a + v.total, 0);
                                return (
                                    <div key={key}>
                                        <div className={`flex items-center justify-between px-5 py-2.5 text-xs font-bold border-b border-t ${COLOR[meta.color]}`}>
                                            <span className="flex items-center gap-1.5">{meta.icon} {meta.label} <span className="font-normal opacity-70">({grupo.length} venta{grupo.length !== 1 ? 's' : ''})</span></span>
                                            <span>Gs. {fmt(subtotal)}</span>
                                        </div>
                                        <div className="divide-y divide-slate-100 dark:divide-white/5 max-h-48 overflow-y-auto">
                                            {grupo.map(v => (
                                                <div key={v.id} className="flex items-center gap-3 px-5 py-2.5 text-sm">
                                                    <span className="font-mono text-brand-800 w-10 shrink-0 text-xs">#{v.id}</span>
                                                    <span className="text-slate-500 dark:text-slate-400 flex-1 min-w-0 truncate text-xs">{v.cliente}</span>
                                                    <span className="text-slate-400 text-xs shrink-0">{v.vendedor}</span>
                                                    <span className="text-xs text-slate-400 shrink-0">{new Date(v.fecha).toLocaleDateString('es-PY')}</span>
                                                    <span className="font-bold text-slate-900 dark:text-white text-xs shrink-0">Gs. {fmt(v.total)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Ventas por día — barra simple */}
                    {data.por_dia.length > 0 && (
                        <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl p-5 shadow-sm">
                            <h3 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-5">
                                Ventas por Día
                            </h3>
                            <div className="space-y-2">
                                {data.por_dia.map(d => {
                                    const pct = Math.max(Math.round((d.total / maxDia) * 100), 2);
                                    return (
                                        <div key={d.fecha} className="flex items-center gap-3 text-sm">
                                            <span className="text-slate-400 dark:text-slate-500 w-24 shrink-0 text-xs font-mono">
                                                {new Date(d.fecha + 'T00:00:00').toLocaleDateString('es-PY', { day: '2-digit', month: 'short' })}
                                            </span>
                                            <div className="flex-1 bg-slate-100 dark:bg-white/5 rounded-full h-6 overflow-hidden">
                                                <div
                                                    className="h-full bg-brand-500 dark:bg-brand-400 rounded-full flex items-center justify-end pr-2 transition-all"
                                                    style={{ width: `${pct}%` }}
                                                >
                                                    {pct > 20 && <span className="text-white text-xs font-bold">{d.cantidad}</span>}
                                                </div>
                                            </div>
                                            <span className="text-slate-700 dark:text-slate-300 font-bold text-xs w-32 text-right shrink-0">
                                                Gs. {fmt(d.total)}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Por vendedor */}
                    {data.por_vendedor.length > 0 && (
                        <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm">
                            <div className="px-5 py-4 border-b border-slate-100 dark:border-white/10 flex items-center gap-2">
                                <Users size={16} className="text-slate-400" />
                                <h3 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Por Vendedor</h3>
                            </div>
                            <div className="tabla-filas">
                                {data.por_vendedor.map((v, i) => {
                                    const pct = data.total_ventas > 0 ? Math.round((v.total / data.total_ventas) * 100) : 0;
                                    return (
                                        <div key={i} className="flex items-center gap-4 px-5 py-4">
                                            <div className="w-8 h-8 rounded-full bg-brand-50 dark:bg-brand-500/10 border border-brand-200 dark:border-brand-500/20 flex items-center justify-center text-brand-800 dark:text-brand-400 font-bold text-sm shrink-0">
                                                {v.nombre.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-semibold text-slate-900 dark:text-white text-sm truncate">{v.nombre}</p>
                                                <p className="text-xs text-slate-400 dark:text-slate-500 truncate">{v.email}</p>
                                            </div>
                                            <div className="text-right shrink-0">
                                                <p className="font-bold text-slate-900 dark:text-white text-sm">Gs. {fmt(v.total)}</p>
                                                <p className="text-xs text-slate-400">{v.cantidad} venta{v.cantidad !== 1 ? 's' : ''} · {pct}%</p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {data.cantidad_ventas === 0 && (
                        <div className="estado-vacio">
                            <BarChart2 size={48} className="mx-auto mb-3 opacity-30" />
                            <p className="font-semibold">Sin ventas en este período</p>
                            <p className="text-sm mt-1">Probá con otro rango de fechas.</p>
                        </div>
                    )}
                </>
            )}

            {dataVar && tab === 'variacion' && (
                <>
                    {/* KPIs */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                        <KpiCard label="Total Cambios" value={dataVar.total_cambios} icon={<BarChart2 size={20} />} color="marca" />
                        <KpiCard label="Productos Afectados" value={dataVar.productos_afectados} icon={<Package size={20} />} color="marca" />
                        <KpiCard
                            label="Sin ajuste de venta"
                            value={dataVar.alerta_sin_ajuste_venta}
                            icon={<AlertTriangle size={20} />}
                            color={dataVar.alerta_sin_ajuste_venta > 0 ? 'orange' : 'slate'}
                            className="col-span-2 sm:col-span-1"
                        />
                    </div>

                    {/* Alerta explicativa si hay productos con costo actualizado pero sin ajuste de venta */}
                    {dataVar.alerta_sin_ajuste_venta > 0 && (
                        <div className="bg-alerta-50 dark:bg-alerta-500/10 border border-alerta-200 dark:border-alerta-500/20 rounded-2xl p-4 flex gap-3">
                            <AlertTriangle size={18} className="text-alerta-500 shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-bold text-alerta-700 dark:text-alerta-400">
                                    {dataVar.alerta_sin_ajuste_venta} producto{dataVar.alerta_sin_ajuste_venta !== 1 ? 's' : ''} actualizaron su precio de costo pero NO el precio de venta en este período.
                                </p>
                                <p className="text-xs text-alerta-600 dark:text-alerta-500 mt-0.5">
                                    Revisá las filas marcadas en naranja y ajustá el precio de venta si corresponde.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Filtro tipo */}
                    <div className="flex gap-2">
                        {[
                            { k: 'todos', label: 'Todos' },
                            { k: 'costo', label: 'Precio de Costo' },
                            { k: 'venta', label: 'Precio de Venta' },
                        ].map(({ k, label }) => (
                            <button
                                key={k}
                                onClick={() => setFiltroTipo(k)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${filtroTipo === k
                                    ? 'bg-brand-600 text-negro-950 border-brand-600'
                                    : 'border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10'}`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    {/* Tabla */}
                    {dataVar.total_cambios === 0 ? (
                        <div className="estado-vacio">
                            <Tag size={48} className="mx-auto mb-3 opacity-30" />
                            <p className="font-semibold">Sin cambios de precio en este período</p>
                        </div>
                    ) : (
                        <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left">
                                    <thead>
                                        <tr className="bg-slate-50 dark:bg-white/5 text-slate-500 dark:text-slate-400 border-b border-slate-100 dark:border-white/10 text-[11px] font-bold uppercase tracking-wider">
                                            <th className="px-5 py-3">Producto</th>
                                            <th className="px-5 py-3">Categoría</th>
                                            <th className="px-5 py-3">Tipo</th>
                                            <th className="px-5 py-3">Proveedor</th>
                                            <th className="px-5 py-3 text-right">Precio Anterior</th>
                                            <th className="px-5 py-3 text-right">Precio Nuevo</th>
                                            <th className="px-5 py-3 text-right">Variación</th>
                                            <th className="px-5 py-3">Usuario</th>
                                            <th className="px-5 py-3">Fecha</th>
                                        </tr>
                                    </thead>
                                    <tbody className="tabla-filas">
                                        {dataVar.cambios
                                            .filter(c => filtroTipo === 'todos' || c.tipo === filtroTipo)
                                            .map(c => (
                                                <tr
                                                    key={c.id}
                                                    className={`transition-colors ${c.alerta_venta
                                                        ? 'bg-alerta-50/60 dark:bg-alerta-500/5 hover:bg-alerta-50 dark:hover:bg-alerta-500/10'
                                                        : 'hover:bg-slate-50/60 dark:hover:bg-white/5'}`}
                                                >
                                                    <td className="px-5 py-3">
                                                        <div className="flex items-center gap-2">
                                                            {c.alerta_venta && <AlertTriangle size={13} className="text-alerta-500 shrink-0" />}
                                                            <span className="font-semibold text-slate-800 dark:text-white">{c.nombre}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-5 py-3 text-slate-500 dark:text-slate-400 text-xs">
                                                        {c.categoria || <span className="italic opacity-50">Sin cat.</span>}
                                                    </td>
                                                    <td className="px-5 py-3">
                                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${c.tipo === 'costo'
                                                            ? 'bg-brand-100 dark:bg-brand-500/15 text-brand-800 dark:text-brand-400'
                                                            : 'bg-exito-100 dark:bg-exito-500/15 text-exito-600 dark:text-exito-400'}`}>
                                                            {c.tipo_display}
                                                        </span>
                                                    </td>
                                                    <td className="px-5 py-3 text-slate-500 dark:text-slate-400 text-xs">
                                                        {c.tipo === 'costo'
                                                            ? (c.proveedor || <span className="italic opacity-40">—</span>)
                                                            : <span className="italic opacity-30">—</span>
                                                        }
                                                    </td>
                                                    <td className="px-5 py-3 text-right font-mono text-slate-500 dark:text-slate-400 text-xs">
                                                        Gs. {fmt(c.precio_anterior)}
                                                    </td>
                                                    <td className="px-5 py-3 text-right font-mono font-bold text-slate-800 dark:text-white text-xs">
                                                        Gs. {fmt(c.precio_nuevo)}
                                                    </td>
                                                    <td className="px-5 py-3 text-right">
                                                        <span className={`font-bold text-xs ${c.variacion_pct > 0
                                                            ? 'text-error-500'
                                                            : c.variacion_pct < 0
                                                                ? 'text-exito-500'
                                                                : 'text-slate-400'}`}>
                                                            {c.variacion_pct > 0 ? '+' : ''}{c.variacion_pct}%
                                                        </span>
                                                    </td>
                                                    <td className="px-5 py-3 text-slate-500 dark:text-slate-400 text-xs">{c.usuario}</td>
                                                    <td className="px-5 py-3 text-slate-500 dark:text-slate-400 text-xs whitespace-nowrap">
                                                        {new Date(c.fecha).toLocaleString('es-PY', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                                    </td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </>
            )}

            {dataAnul && tab === 'anulaciones' && (
                <>
                    {/* KPIs anulaciones */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <KpiCard
                            label="Total Anulaciones"
                            value={dataAnul.total_anulaciones}
                            icon={<XCircle size={20} />}
                            color="marca"
                        />
                        <KpiCard
                            label="Monto Anulado"
                            value={`Gs. ${fmt(dataAnul.monto_total_anulado)}`}
                            icon={<AlertTriangle size={20} />}
                            color="problema"
                        />
                    </div>

                    {dataAnul.total_anulaciones === 0 && (
                        <div className="estado-vacio">
                            <XCircle size={48} className="mx-auto mb-3 opacity-30" />
                            <p className="font-semibold">Sin anulaciones en este período</p>
                        </div>
                    )}

                    {dataAnul.anulaciones.length > 0 && (
                        <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm">
                            <div className="px-5 py-4 border-b border-slate-100 dark:border-white/10">
                                <h3 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Detalle de Anulaciones</h3>
                            </div>
                            <div className="tabla-filas">
                                {dataAnul.anulaciones.map(a => (
                                    <div key={a.id} className="px-5 py-4 space-y-2">
                                        <div className="flex items-start justify-between gap-3">
                                            <div>
                                                <span className="font-mono text-error-400 font-bold text-sm">#{a.id}</span>
                                                <span className="mx-2 text-slate-300 dark:text-slate-600">·</span>
                                                <span className="text-slate-600 dark:text-slate-400 text-sm">{new Date(a.fecha).toLocaleString('es-PY')}</span>
                                            </div>
                                            <span className="font-bold text-slate-900 dark:text-white text-sm shrink-0">Gs. {fmt(a.total)}</span>
                                        </div>
                                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
                                            <span>Cliente: <strong className="text-slate-700 dark:text-slate-300">{a.cliente}</strong></span>
                                            <span>Vendedor: <strong className="text-slate-700 dark:text-slate-300">{a.vendedor}</strong></span>
                                            <span className={`px-2 py-0.5 rounded-full border text-xs font-semibold ${COLOR[FORMA_META[a.forma_pago]?.color] || ''}`}>
                                                {FORMA_META[a.forma_pago]?.label || a.forma_pago}
                                            </span>
                                        </div>
                                        {a.motivo_anulacion && (
                                            <div className="bg-error-50 dark:bg-error-500/10 border border-error-100 dark:border-error-500/20 rounded-lg px-3 py-2">
                                                <span className="text-xs font-bold text-error-500 dark:text-error-400 uppercase">Motivo: </span>
                                                <span className="text-xs text-error-700 dark:text-error-300">{a.motivo_anulacion}</span>
                                            </div>
                                        )}
                                        <div className="pl-2 space-y-0.5">
                                            {a.detalles.map((d, i) => (
                                                <p key={i} className="text-xs text-slate-400 dark:text-slate-500">
                                                    {d.producto} × {d.cantidad} — Gs. {fmt(d.subtotal)}
                                                </p>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}

            {tab === 'productos' && (
                <>
                    {/* Qué hay, cuánto vale y qué falta. Son las tres preguntas
                        que se contestan mirando un inventario. */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <KpiCard
                            label="Artículos"
                            value={dataProd?.total_articulos ?? '—'}
                            icon={<Boxes size={20} />}
                            color="marca"
                        />
                        <KpiCard
                            label="Unidades en stock"
                            value={dataProd ? fmt(dataProd.unidades_en_stock) : '—'}
                            icon={<Package size={20} />}
                            color="marca"
                        />
                        <KpiCard
                            label="Valor al costo"
                            value={dataProd ? `Gs. ${fmt(dataProd.valor_total_costo)}` : '—'}
                            icon={<Banknote size={20} />}
                            color="neutro"
                        />
                        <KpiCard
                            label="A reponer"
                            value={dataProd?.a_reponer ?? '—'}
                            icon={<AlertTriangle size={20} />}
                            color={dataProd?.a_reponer > 0 ? 'problema' : 'marca'}
                        />
                    </div>

                    <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-2xl shadow-sm overflow-hidden">
                        <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-slate-200 dark:border-white/10">
                            <div>
                                <h3 className="titulo-item">Listado de artículos</h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                    Es una foto de hoy: no depende del período elegido arriba.
                                </p>
                            </div>
                            <div className="flex items-center gap-3">
                                <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={soloReponer}
                                        onChange={e => setSoloReponer(e.target.checked)}
                                        className="w-4 h-4 accent-brand-500"
                                    />
                                    Solo lo que hay que reponer
                                </label>
                                <button
                                    type="button"
                                    onClick={() => openListaProductos(dataProd, settings)}
                                    disabled={!dataProd}
                                    className="flex items-center gap-2 bg-brand-500 hover:bg-brand-400 disabled:opacity-40 text-negro-950 px-4 py-2 rounded-xl text-sm font-bold transition-colors"
                                >
                                    <Printer size={16} />
                                    Imprimir
                                </button>
                            </div>
                        </div>

                        {!dataProd ? (
                            <div className="cargando py-12">
                                <RefreshCw className="animate-spin text-brand-800" size={24} />
                            </div>
                        ) : dataProd.productos.length === 0 ? (
                            <div className="estado-vacio py-12">
                                <Boxes size={48} className="mx-auto mb-3 opacity-30" />
                                <p className="font-semibold">
                                    {soloReponer ? 'No hay nada para reponer' : 'No hay artículos cargados'}
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="tabla-encabezado">
                                        <tr>
                                            <th className="px-4 py-3 text-left">Artículo</th>
                                            <th className="px-4 py-3 text-left hidden md:table-cell">Categoría</th>
                                            <th className="px-4 py-3 text-right">Costo</th>
                                            <th className="px-4 py-3 text-right">Venta</th>
                                            <th className="px-4 py-3 text-center">En stock</th>
                                            <th className="px-4 py-3 text-right hidden lg:table-cell">Valor al costo</th>
                                        </tr>
                                    </thead>
                                    <tbody className="tabla-filas">
                                        {dataProd.productos.map(p => (
                                            <tr key={p.id} className={p.a_reponer ? 'bg-alerta-50 dark:bg-alerta-500/5' : ''}>
                                                <td className="px-4 py-3">
                                                    <div className="font-semibold text-slate-900 dark:text-white">
                                                        {p.nombre}
                                                        {p.marca && <span className="text-slate-400 dark:text-gray-500 font-normal"> · {p.marca}</span>}
                                                    </div>
                                                    <div className="text-xs font-mono text-slate-400 dark:text-gray-600">{p.codigo_barras}</div>
                                                </td>
                                                <td className="px-4 py-3 text-slate-500 dark:text-slate-400 hidden md:table-cell">
                                                    {p.categoria || '—'}
                                                </td>
                                                <td className="px-4 py-3 text-right font-mono text-slate-500 dark:text-slate-400">
                                                    {fmt(p.costo_compra)}
                                                </td>
                                                <td className="px-4 py-3 text-right font-mono font-bold text-slate-900 dark:text-white">
                                                    {fmt(p.precio_venta)}
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <span className={`font-bold ${p.a_reponer ? 'text-alerta-600 dark:text-alerta-400' : 'text-slate-900 dark:text-white'}`}>
                                                        {p.stock_actual}
                                                    </span>
                                                    <div className="text-[10px] text-slate-400 dark:text-gray-600">mínimo {p.stock_minimo}</div>
                                                </td>
                                                <td className="px-4 py-3 text-right font-mono text-slate-500 dark:text-slate-400 hidden lg:table-cell">
                                                    {fmt(p.valor_costo)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

function KpiCard({ label, value, icon, color, className = '' }) {
    return (
        <div className={`p-5 rounded-2xl border ${COLOR[color]} ${className}`}>
            <div className="flex items-center gap-2 mb-2 opacity-70">{icon} <span className="text-xs font-bold uppercase tracking-wide">{label}</span></div>
            <p className="text-2xl font-bold">{value}</p>
        </div>
    );
}
